import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { shopsData } from '../../mocks/shops';

const ShopsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const categories = ['All', 'Food', 'Café', 'Craft', 'Bar', 'Community'];

  const filteredShops = shopsData.filter(shop => {
    const matchesCategory = selectedCategory === 'All' || shop.category === selectedCategory;
    const matchesSearch = shop.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         shop.area.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         shop.catchphrase.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  useEffect(() => {
    // Add Schema.org JSON-LD for ItemList
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "琉球人オーナーのお店一覧",
      "description": "沖縄・琉球人オーナーが営むお店の一覧です。",
      "numberOfItems": shopsData.length,
      "itemListElement": shopsData.map((shop, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "LocalBusiness",
          "name": shop.name,
          "description": shop.catchphrase,
          "address": {
            "@type": "PostalAddress",
            "addressLocality": shop.area,
            "addressRegion": "沖縄県",
            "addressCountry": "JP"
          }
        }
      }))
    });
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF8F3] via-[#FFFBF7] to-[#FFF5ED]">
      <Header />

      <div className="pt-[96px] pb-24 px-6">
        <div className="max-w-[1240px] mx-auto">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mb-16 text-center"
          >
            <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-gradient-to-r from-[#FFE8DC] to-[#FFF0E6] mb-5">
              <span className="text-[13px] font-bold bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] bg-clip-text text-transparent tracking-[1.5px] uppercase" style={{ fontFamily: "'Inter', sans-serif" }}>
                ✦ ALL SHOPS
              </span>
            </div>
            <h1 className="text-[42px] font-bold bg-gradient-to-r from-[#19425B] to-[#1F7A8C] bg-clip-text text-transparent leading-[1.3] mb-4" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              お店一覧
            </h1>
            <p className="text-[16px] text-[rgba(25,66,91,0.65)] leading-relaxed max-w-[600px] mx-auto" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              琉球人オーナーが営む、こだわりのお店をご紹介します。
            </p>
          </motion.div>

          {/* Search and Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-12"
          >
            {/* Search Bar */}
            <div className="mb-8">
              <div className="relative max-w-[560px] mx-auto">
                <i className="ri-search-line absolute left-5 top-1/2 -translate-y-1/2 text-[22px] text-[#FF9E7A]"></i>
                <input
                  type="text"
                  placeholder="お店を検索..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-14 pr-6 py-4 rounded-full border-2 border-[rgba(255,158,122,0.2)] bg-white text-[15px] text-[#19425B] placeholder:text-[rgba(25,66,91,0.4)] focus:outline-none focus:border-[#FF9E7A] focus:shadow-[0_8px_24px_rgba(255,158,122,0.15)] transition-all duration-300"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.3 + index * 0.1, duration: 0.5 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-full text-[14px] font-semibold transition-all duration-400 hover:-translate-y-1 hover:scale-105 whitespace-nowrap cursor-pointer ${
                    selectedCategory === category
                      ? 'bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] text-white shadow-[0_8px_24px_rgba(255,158,122,0.4)]'
                      : 'bg-white border-[1.5px] border-[#FFD4C0] text-[#FF9E7A] hover:bg-white hover:shadow-[0_6px_20px_rgba(255,158,122,0.25)]'
                  }`}
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  {category}
                </motion.button>
              ))}
            </div>
          </motion.div>

          {/* Results Count */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mb-8 text-center"
          >
            <p className="text-[15px] text-[rgba(25,66,91,0.65)] font-medium" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
              <span className="text-[#FF9E7A] font-bold text-[18px]">{filteredShops.length}</span>件のお店が見つかりました
            </p>
          </motion.div>

          {/* Shop Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredShops.map((shop, index) => (
              <motion.div
                key={shop.id}
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.08, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={`/shop/${shop.id}`}
                  className="group block bg-white rounded-[24px] overflow-hidden shadow-[0_8px_32px_rgba(255,158,122,0.12)] hover:shadow-[0_24px_64px_rgba(255,158,122,0.25)] hover:-translate-y-3 transition-all duration-500 cursor-pointer"
                >
                  {/* Image */}
                  <div className="relative w-full h-72 overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-t from-[rgba(255,158,122,0.15)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10"></div>
                    <img
                      src={shop.image}
                      alt={shop.name}
                      className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>

                  {/* Content */}
                  <div className="p-7">
                    <h3 className="text-[22px] font-bold text-[#19425B] mb-3 group-hover:text-[#FF9E7A] transition-colors duration-300" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {shop.name}
                    </h3>
                    <div className="flex items-center gap-2.5 mb-4">
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E]"></div>
                        <span className="text-[14px] text-[rgba(31,122,140,0.75)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                          {shop.area}
                        </span>
                      </div>
                      <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-[#FFE8DC] to-[#FFF0E6] text-[13px] font-semibold bg-gradient-to-r from-[#FF9E7A] to-[#FFBC9E] bg-clip-text text-transparent">
                        {shop.category}
                      </span>
                    </div>
                    <p className="text-[15px] leading-[1.8] text-[rgba(25,66,91,0.65)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                      {shop.catchphrase}
                    </p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* No Results */}
          {filteredShops.length === 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-24"
            >
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#FFE8DC] to-[#FFF0E6] flex items-center justify-center">
                <i className="ri-store-3-line text-[40px] text-[#FF9E7A]"></i>
              </div>
              <p className="text-[20px] font-semibold text-[rgba(25,66,91,0.6)] mb-2" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                お店が見つかりませんでした
              </p>
              <p className="text-[15px] text-[rgba(25,66,91,0.5)]" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                別のキーワードで検索してみてください
              </p>
            </motion.div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShopsPage;
